import { useEffect, useMemo, useState } from "react";
import { Clock3, Pickaxe, Volume2, VolumeX } from "lucide-react";
import { useMiningGame } from "../hooks/useMiningGame";
import { MiningArena } from "./mining/MiningArena";
import { WagonProgress } from "./mining/WagonProgress";


function formatClock(totalSeconds) {
  const seconds = Math.max(0, totalSeconds);
  const hours = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const rest = String(seconds % 60).padStart(2, "0");
  return `${hours}:${minutes}:${rest}`;
}


function playStrikeSound() {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = "triangle";
  oscillator.frequency.setValueAtTime(760, context.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(150, context.currentTime + 0.09);
  gain.gain.setValueAtTime(0.08, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.12);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.12);
  oscillator.addEventListener("ended", () => context.close());
}


export const MiningGame = ({ onBalanceChange }) => {
  const { game, loading, striking, error, lastReward, strike, reload } = useMiningGame(onBalanceChange);
  const [soundOn, setSoundOn] = useState(true);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!game?.cooldown_until) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [game?.cooldown_until]);

  const cooldownSeconds = useMemo(() => {
    if (!game?.cooldown_until) return 0;
    return Math.max(0, Math.ceil((new Date(game.cooldown_until).getTime() - now) / 1000));
  }, [game?.cooldown_until, now]);

  useEffect(() => {
    if (game?.cooldown_until && cooldownSeconds === 0) reload();
  }, [cooldownSeconds, game?.cooldown_until, reload]);

  function handleStrike() {
    if (soundOn && game?.can_strike && !striking) playStrikeSound();
    strike();
  }

  if (loading) return <section id="mining-game" className="mining-game mining-loading" data-testid="mining-game-loading">Loading mine…</section>;

  return (
    <section id="mining-game" className="mining-game" data-testid="mining-game-container" aria-labelledby="mining-game-title">
      <div className="mining-header">
        <div>
          <span className="mining-kicker"><span /> DAILY MINE</span>
          <h2 id="mining-game-title">Strike. Load. Dispatch.</h2>
        </div>
        <button
          className="mining-sound-button"
          onClick={() => setSoundOn((value) => !value)}
          aria-label={soundOn ? "Mute mining sound" : "Enable mining sound"}
          aria-pressed={soundOn}
          data-testid="audio-toggle-button"
        >
          {soundOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </button>
      </div>

      <div className="mining-metrics">
        <div><span>SESSION EARNINGS</span><strong data-testid="mining-reward-total">${game.cycle_earnings.toFixed(3)}</strong></div>
        <div><span>WITHDRAWABLE BALANCE</span><strong data-testid="mining-withdrawable-balance">${game.balance.toFixed(3)}</strong></div>
        <div><span>ACTIVE LOAD</span><strong data-testid="mining-active-wagon">Wagon {game.active_wagon} / 2</strong></div>
      </div>

      <div className="mining-stage-grid">
        <MiningArena striking={striking} lastReward={lastReward} />
        <div className="mining-rail" data-testid="mining-wagon-progress">
          <div className="mining-rail-title">
            <span>WAGON CAPACITY</span>
            <b data-testid="mining-strike-count">{game.wagon_strikes} / 50 strikes</b>
          </div>
          <WagonProgress number={1} progress={game.wagon_1_progress} active={game.active_wagon === 1 && game.can_strike} dispatched={game.completed_wagons >= 1} />
          <WagonProgress number={2} progress={game.wagon_2_progress} active={game.active_wagon === 2 && game.can_strike} dispatched={game.completed_wagons >= 2} />
          {cooldownSeconds > 0 && (
            <div className="mining-cooldown" data-testid="cooldown-timer" role="status">
              <Clock3 size={18} />
              <div><span>NEXT SHIFT</span><strong>{formatClock(cooldownSeconds)}</strong></div>
            </div>
          )}
        </div>
      </div>

      {error && <div className="mining-error" role="alert" data-testid="mining-game-error">{error}</div>}
      <button
        className="mining-strike-button"
        onClick={handleStrike}
        disabled={striking || !game.can_strike || cooldownSeconds > 0}
        aria-keyshortcuts="Space Enter"
        data-testid="mining-strike-button"
      >
        <Pickaxe size={22} />
        <span>{cooldownSeconds > 0 ? `Mine reopens in ${formatClock(cooldownSeconds)}` : striking ? "Striking…" : "Strike pickaxe"}</span>
        <small>{game.can_strike ? `${game.strikes_remaining} strikes left` : "Two wagons dispatched"}</small>
      </button>
    </section>
  );
};
